import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtEUR, fmtDateTime, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import StatusBadge from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { openAuthedFile } from "@/lib/openFile";
import TeamSection from "@/components/TeamSection";
import TasksSection from "@/components/TasksSection";
import Comments from "@/components/Comments";
import { toast } from "sonner";
import { ArrowLeft, Copy, Lock, XCircle, CheckCircle2, Mail, Clock, FlaskConical, FileDown, Link2, RotateCw, ExternalLink, FileText, Receipt, Undo2, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import AttachmentsSection from "@/components/AttachmentsSection";
import { FileImportStatus } from "@/components/FileImportStatus";
import EmailHistorySection from "@/components/EmailHistorySection";
import { VersandJobBanner } from "@/components/VersandJobBanner";
import { VersandmitteilungHeaderButton } from "@/components/VersandmitteilungHeaderButton";
import { RechnungActions } from "@/components/RechnungActions";
import { RechnungErstellenDialog } from "@/components/RechnungErstellenDialog";
import { GesamtrabattSummen } from "@/components/GesamtrabattSummen";
import { DokumentVorschauDialog } from "@/components/DokumentVorschau";
import { KostenlosBadge, KostenlosSevdeskWarnung, KEINE_RECHNUNG, CRM_HINWEIS } from "@/components/Kostenlos";
import { RechnungEntwurfBanner } from "@/components/RechnungEntwurfBanner";
import { RechnungSevdeskStatus } from "@/components/RechnungSevdeskStatus";
import { OrderShipmentsSection } from "@/components/OrderShipmentsSection";
import { AddShippingButton } from "@/components/AddShippingButton";
import { ProduktionSection } from "@/components/ProduktionSection";
import { KundenteileSection } from "@/components/KundenteileSection";
import { VERSAND_STATUS_LABEL } from "@/lib/produktion";
import { DruckparameterCard } from "@/components/DruckparameterCard";
import { ZahlungsfristBox } from "@/components/ZahlungsfristBox";
import { KalkulationDialog } from "@/components/KalkulationDialog";
import { PositionsTable } from "@/components/PositionsTable";
import { SectionBody, SectionCollapseProvider, SectionHead, SectionSummary, useOrderUiState } from "@/components/SectionCollapse";
import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";

// Alle ein-/ausklappbaren Bereiche der Auftragsdetailseite
const SECTION_KEYS = ["zahlungslink", "portal", "zahlungsfrist", "druckparameter", "produktion", "kundenteile", "positionen", "notiz", "dateien", "emails", "versand", "team", "aufgaben", "kommentare"];
import { useKalkulation } from "@/hooks/useKalkulation";
import { CancelOrderDialog, PaidCancelBadge, isOrderPaid } from "@/components/CancelOrderDialog";

const RawRequestBlock = ({ raw }) => {
  let pretty = raw;
  try { pretty = JSON.stringify(JSON.parse(raw), null, 2); } catch { /* gekürzt → roh anzeigen */ }
  return (
    <div className="mt-2" data-testid="auftrag-sevdesk-raw-request">
      <div className="flex items-center justify-between">
        <b>Gesendete Daten (Raw Request)</b>
        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { navigator.clipboard.writeText(pretty); toast.success("Kopiert."); }} data-testid="auftrag-sevdesk-raw-copy">Kopieren</Button>
      </div>
      <pre className="mt-1 p-2 bg-white border border-amber-200 rounded max-h-72 overflow-auto text-[11px] font-mono text-slate-800 whitespace-pre-wrap break-all">{pretty}</pre>
    </div>
  );
};

export default function AuftragDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const user = getUser();
  const [order, setOrder] = useState(null);
  const [mailTick, setMailTick] = useState(0); // E-Mail-Historie nach Versandmitteilung neu laden
  const [confirmFreigabe, setConfirmFreigabe] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [respectBh, setRespectBh] = useState(true);
  const [testMode, setTestMode] = useState(false);
  const [allowLocalPdf, setAllowLocalPdf] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [portalInfo, setPortalInfo] = useState(null);
  const [resettingPortal, setResettingPortal] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState("in_bearbeitung");
  const [reactivating, setReactivating] = useState(false);
  const [resyncingSev, setResyncingSev] = useState(false);
  const [applyingLp, setApplyingLp] = useState(false);

  const [kunde, setKunde] = useState(null);
  const [kalkAtt, setKalkAtt] = useState(null);
  const posQueue = useRef(Promise.resolve()); // Positions-Änderungen nacheinander senden (siehe updatePos)
  const pendingPos = useRef({});
  const kalk = useKalkulation(id, () => load());
  const ui = useOrderUiState(id);
  const load = async () => {
    try {
      const r = await api.get(`/auftraege/${id}`);
      setOrder(r.data);
      try { const kr = await api.get(`/kunden/${r.data.kunde_id}`); setKunde(kr.data); } catch { /* ignore */ }
    }
    catch (e) { toast.error("Auftrag nicht gefunden."); nav("/auftraege"); }
  };
  const loadPortal = async () => {
    try { const r = await api.get(`/auftraege/${id}/portal`); setPortalInfo(r.data); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); loadPortal();  }, [id]);

  const resetPortal = async () => {
    setResettingPortal(true);
    try {
      const r = await api.post(`/auftraege/${id}/portal/reset`);
      setPortalInfo({ ...r.data, confirmed_at: null, confirmed_ip: null });
      toast.success("Portal-Link neu erzeugt. Alte Adressbestätigung wurde zurückgesetzt.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setResettingPortal(false); }
  };
  const copyPortal = () => {
    if (!portalInfo?.portal_url) return;
    navigator.clipboard.writeText(portalInfo.portal_url);
    toast.success("Portal-Link kopiert.");
  };

  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [offerPreview, setOfferPreview] = useState(false);
  // fertigstellen: true/false bei neuer Rechnung (Dialog), undefined = vorhandene Rechnung → PDF neu laden
  const createInvoice = async (fertigstellen) => {
    if (invoiceBusy) return;
    setInvoiceBusy(true);
    try {
      const r = await api.post(`/auftraege/${id}/rechnung`, fertigstellen === undefined ? undefined : { fertigstellen });
      toast.success(fertigstellen === undefined ? `Rechnung ${r.data.rechnungsnummer}: PDF neu geladen.`
        : r.data.entwurf ? `Rechnung ${r.data.rechnungsnummer} als Entwurf in sevDesk erstellt.` : `Rechnung ${r.data.rechnungsnummer} erstellt und fertiggestellt.`);
      setInvoiceDialog(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rechnung konnte nicht erstellt werden."); setInvoiceDialog(false); await load(); }
    finally { setInvoiceBusy(false); }
  };

  const doReactivate = async () => {
    setReactivating(true);
    try {
      await api.post(`/auftraege/${id}/reaktivieren`, { target_status: reactivateTarget });
      toast.success("Auftrag reaktiviert.");
      setReactivateOpen(false);
      await load();
      await loadPortal();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reaktivierung fehlgeschlagen."); }
    finally { setReactivating(false); }
  };

  const resyncSevdesk = async () => {
    setResyncingSev(true);
    try {
      await api.post(`/auftraege/${id}/sevdesk/resync`);
      toast.success("sevDesk-Sync erfolgreich.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "sevDesk-Sync fehlgeschlagen."); }
    finally { setResyncingSev(false); }
  };

  const reapplyLetterpaper = async () => {
    setApplyingLp(true);
    try {
      const r = await api.post(`/auftraege/${id}/sevdesk/briefpapier`);
      toast.success(`Briefpapier angewendet – Angebot-PDF v${r.data.angebot_pdf_version} gespeichert.`);
      if (r.data.warning) toast.warning(r.data.warning);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Briefpapier konnte nicht angewendet werden."); await load(); }
    finally { setApplyingLp(false); }
  };

  if (!order || !ui.loaded) return <div className="text-slate-500">Lade…</div>;

  const locked = order.price_locked;
  const isViewer = user?.rolle === "viewer";
  const canFreigabe = !isViewer && (order.status === "neu" || order.status === "in_bearbeitung") && order.positionen.length > 0 && order.positionen.every((p) => Number(p.einzelpreis_netto) > 0);

  const addPos = async (payload) => {
    try {
      const r = await api.post(`/auftraege/${id}/positionen`, payload);
      setOrder(r.data);
      toast.success("Position hinzugefügt.");
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); return false; }
  };

  // Änderungen nacheinander senden; Folge-Änderungen derselben Zeile bauen auf der Server-Antwort auf
  // (z. B. Beschreibung per Blur + sofort Preis per Enter → keine Überschreibung mit veraltetem Stand).
  const updatePos = (pos, fields) => {
    const entry = pendingPos.current[pos.id] || (pendingPos.current[pos.id] = { count: 0, pos: null });
    entry.count += 1;
    posQueue.current = posQueue.current.then(async () => {
      const body = { ...(entry.pos || pos), ...fields };
      try {
        const r = await api.patch(`/auftraege/${id}/positionen/${pos.id}`, body);
        setOrder(r.data);
        entry.pos = r.data.positionen?.find((x) => x.id === pos.id) || body;
      } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
      finally { entry.count -= 1; if (!entry.count) delete pendingPos.current[pos.id]; }
    });
    return posQueue.current;
  };

  const deletePos = async (pos) => {
    if (!window.confirm("Position löschen?")) return;
    try { const r = await api.delete(`/auftraege/${id}/positionen/${pos.id}`); setOrder(r.data); toast.success("Position gelöscht."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const doFreigabe = async () => {
    setReleasing(true);
    try {
      const r = await api.post(`/auftraege/${id}/preisfreigabe`, {
        send_email: sendEmail,
        to_email: toEmail,
        subject,
        respect_business_hours: respectBh,
        test_mode: testMode,
        allow_local_pdf: allowLocalPdf,
      });
      setOrder(r.data);
      setPreview(null);
      if (r.data.email_warning) {
        toast.warning(r.data.email_warning);
      } else if (r.data.kostenlos) {
        toast.success(`${sendEmail ? "Preise freigegeben. CRM-Angebot erstellt und E-Mail geplant." : "Preise freigegeben. CRM-Angebot erstellt."}${r.data.status === "bezahlt" ? " Keine Zahlung nötig – Auftrag ist freigegeben." : " Der Kunde bestätigt seine Adresse im Portal."}`);
      } else {
        toast.success(sendEmail ? "Preise freigegeben. Zahlungslink erstellt und E-Mail geplant." : "Preise freigegeben. Zahlungslink erstellt.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Preisfreigabe fehlgeschlagen."); load(); }
    finally { setReleasing(false); }
  };

  const openPreisfreigabe = async () => {
    try {
      const r = await api.get(`/auftraege/${id}/preisfreigabe/vorschau`);
      setPreview(r.data);
      setToEmail(r.data.to_email || "");
      setSubject(r.data.subject);
      setSendEmail(r.data.smtp_configured);
      setRespectBh(!r.data.is_within_business_hours);
      setTestMode(false);
      setAllowLocalPdf(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen."); }
  };


  const copyLink = () => {
    if (!order.stripe_payment_link_url) return;
    navigator.clipboard.writeText(order.stripe_payment_link_url);
    toast.success("Zahlungslink kopiert.");
  };

  return (
    <SectionCollapseProvider ui={ui}>
    <div className="space-y-6" data-testid="auftrag-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => nav("/auftraege")} className="mb-2 -ml-2"><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-xs text-slate-500">{order.auftragsnummer}</span>
            <StatusBadge status={order.status} testid="auftrag-status-badge"
              label={order.kostenlos ? ({ wartet_auf_zahlung: "Wartet auf Adressbestätigung", bezahlt: "Freigegeben (kostenlos)" }[order.status]) : undefined} />
            {order.versand_status && <span className={`badge-status ${order.versand_status === "versendet" ? "badge-abgeschlossen" : "badge-wartet_auf_zahlung"}`} data-testid="auftrag-versand-status">{VERSAND_STATUS_LABEL[order.versand_status]}</span>}
            <PaidCancelBadge order={order} />
            <KostenlosBadge order={order} />
            {locked && <span className="badge-status badge-neu"><Lock className="w-3 h-3" /> Preise gesperrt</span>}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-2" data-testid="auftrag-titel">{order.titel}</h1>
          {(order.needs_review || order.kundendatei_fehlt) && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {order.needs_review && <span className="text-xs px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200" data-testid="auftrag-needs-review">Prüfen: AGB-Zustimmung fehlt</span>}
              {order.kundendatei_fehlt && <span className="text-xs px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200" data-testid="auftrag-file-missing">Kundendatei fehlt</span>}
            </div>
          )}
          <div className="text-sm text-slate-500 mt-1">Kunde: <span className="text-slate-800 font-medium">{order.kunde_name}</span> · angelegt {fmtDateTime(order.created_at)}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isViewer && (order.versand_status || order.status === "abgeschlossen") && <VersandmitteilungHeaderButton order={order} onSent={() => { load(); setMailTick((n) => n + 1); }} />}
          {canFreigabe && (
            <Button onClick={openPreisfreigabe} className="bg-blue-600 hover:bg-blue-700" data-testid="auftrag-preisfreigabe-button">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Preise freigeben
            </Button>
          )}
          {(["admin", "manager"].includes(user?.rolle) || (user?.rolle === "buchhaltung" && isOrderPaid(order))) && order.status !== "storniert" && order.status !== "storniert_timeout" && (
            <Button variant="outline" onClick={() => setConfirmCancel(true)} data-testid="auftrag-cancel-button">
              <XCircle className="w-4 h-4 mr-2" /> Stornieren
            </Button>
          )}
          {(user?.rolle === "admin" || user?.rolle === "manager") && (order.status === "storniert" || order.status === "storniert_timeout") && (
            <Button onClick={() => setReactivateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="auftrag-reactivate-button">
              <Undo2 className="w-4 h-4 mr-2" /> Auftrag reaktivieren
            </Button>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 -mt-3" data-testid="auftrag-collapse-toolbar">
        <Button size="sm" variant="ghost" className="text-slate-600" onClick={() => ui.setCollapsed(SECTION_KEYS, true)} data-testid="auftrag-collapse-all">
          <ChevronsDownUp className="w-4 h-4 mr-1" /> Alle einklappen
        </Button>
        <Button size="sm" variant="ghost" className="text-slate-600" onClick={() => ui.setCollapsed(SECTION_KEYS, false)} data-testid="auftrag-expand-all">
          <ChevronsUpDown className="w-4 h-4 mr-1" /> Alle ausklappen
        </Button>
      </div>

      {order.angebot_pdf_warning && (
        <Card className="border-rose-200 bg-rose-50" data-testid="auftrag-pdf-warning">
          <CardContent className="p-4 flex items-start gap-3 text-sm text-rose-900">
            <AlertTriangle className="w-5 h-5 text-rose-700 shrink-0 mt-0.5" />
            <div><div className="font-semibold">Angebots-PDF-Fehler</div><div>{order.angebot_pdf_warning}</div></div>
          </CardContent>
        </Card>
      )}

      <VersandJobBanner orderId={id} refreshKey={mailTick} onDone={() => { load(); setMailTick((n) => n + 1); }} />
      <KostenlosSevdeskWarnung order={order} canAct={["admin", "manager", "employee", "buchhaltung"].includes(user?.rolle)} onUpdated={setOrder} />

      {order.rechnung_fehler && !order.rechnungsnummer && (
        <Card className="border-rose-200 bg-rose-50" data-testid="auftrag-rechnung-fehler">
          <CardContent className="p-4 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-rose-900">
              <div className="font-semibold">Rechnung konnte nicht erstellt werden</div>
              <div>{order.rechnung_fehler.message}</div>
            </div>
            {["admin", "manager", "buchhaltung"].includes(user?.rolle) && (
              <Button size="sm" variant="outline" onClick={() => setInvoiceDialog(true)} disabled={invoiceBusy} data-testid="auftrag-rechnung-retry">{invoiceBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />} {invoiceBusy ? "Rechnung wird erstellt…" : "Erneut versuchen"}</Button>
            )}
          </CardContent>
        </Card>
      )}
      <RechnungEntwurfBanner order={order} user={user} onChanged={load} />
      <RechnungErstellenDialog orderId={id} open={invoiceDialog} onOpenChange={setInvoiceDialog} busy={invoiceBusy} onConfirm={(f) => createInvoice(f)} />
      {order.storno_rechnung_fehler && (
        <Card className="border-rose-200 bg-rose-50" data-testid="auftrag-stornorechnung-fehler">
          <CardContent className="p-4 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-rose-900">{order.storno_rechnung_fehler}</div>
            {["admin", "manager", "buchhaltung"].includes(user?.rolle) && (
              <Button size="sm" variant="outline" onClick={async () => { try { await api.post(`/auftraege/${id}/stornorechnung`); toast.success("Stornorechnung erstellt."); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); } }} data-testid="auftrag-stornorechnung-retry"><RotateCw className="w-4 h-4 mr-1" /> Erneut versuchen</Button>
            )}
          </CardContent>
        </Card>
      )}

      {order.sevdesk_warning && (
        <Card className="border-amber-200 bg-amber-50" data-testid="auftrag-sevdesk-warning">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-amber-900">
              <div className="font-semibold">sevDesk-Hinweis</div>
              <div>{order.sevdesk_warning}</div>
              {order.sevdesk_angebot_nummer && <div className="text-xs mt-1 font-mono">Angebot {order.sevdesk_angebot_nummer}</div>}
              {order.sevdesk_info && <div className="text-xs mt-1 text-slate-600" data-testid="auftrag-sevdesk-info">{order.sevdesk_info}</div>}
              {order.sevdesk_last_error && (
                <details className="mt-2" data-testid="auftrag-sevdesk-details">
                  <summary className="cursor-pointer text-xs text-amber-800 hover:text-amber-950 select-none">Details anzeigen</summary>
                  <div className="mt-2 space-y-1 text-xs">
                    <div><b>Zeit:</b> {order.sevdesk_last_error.at}</div>
                    <div><b>Aktion:</b> {order.sevdesk_last_error.action} · HTTP {order.sevdesk_last_error.status}</div>
                    <div><b>Fehler:</b> {order.sevdesk_last_error.detail}</div>
                    {order.sevdesk_last_error.raw_response && (
                      <pre className="mt-1 p-2 bg-white border border-amber-200 rounded max-h-56 overflow-auto text-[11px] font-mono text-slate-800 whitespace-pre-wrap break-all">{order.sevdesk_last_error.raw_response}</pre>
                    )}
                    {order.sevdesk_last_error.raw_request && <RawRequestBlock raw={order.sevdesk_last_error.raw_request} />}
                  </div>
                </details>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={resyncSevdesk} disabled={resyncingSev} data-testid="auftrag-sevdesk-resync">
              <RefreshCw className={`w-4 h-4 mr-1 ${resyncingSev ? "animate-spin" : ""}`} /> Erneut versuchen
            </Button>
          </CardContent>
        </Card>
      )}

      {order.stripe_payment_link_url && (
        <Card className="border-amber-200 bg-amber-50" data-testid="auftrag-payment-link-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <SectionHead k="zahlungslink" label="Stripe-Zahlungslink" className="flex items-center gap-1">
                <div className="text-xs uppercase font-mono tracking-wider text-amber-800">Stripe-Zahlungslink</div>
              </SectionHead>
              <SectionBody k="zahlungslink">
                <div className="text-sm font-mono text-slate-800 break-all mt-1" data-testid="auftrag-payment-link-url">{order.stripe_payment_link_url}</div>
              </SectionBody>
            </div>
            <Button variant="outline" onClick={copyLink} data-testid="auftrag-copy-link-button"><Copy className="w-4 h-4 mr-2" /> Kopieren</Button>
          </CardContent>
        </Card>
      )}

      {kunde && kunde.data_complete === false && !portalInfo?.confirmed_at && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="auftrag-kunde-incomplete-hint">
          <span className="shrink-0 mt-0.5">ℹ️</span>
          <div>Kundendaten werden beim ersten Portalbesuch ergänzt. Nach der Preisfreigabe bestätigt der Kunde seine Rechnungsadresse selbst im Angebots-Portal.</div>
        </div>
      )}

      {portalInfo?.portal_url && (
        <Card className="border-blue-200 bg-blue-50" data-testid="auftrag-portal-card">
          <CardContent className="p-4 space-y-3">
            <SectionHead k="portal" label="Angebots-Portal-Link" className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-blue-700" />
              <div className="text-xs uppercase font-mono tracking-wider text-blue-800">Angebots-Portal-Link</div>
              {portalInfo.confirmed_at
                ? <span className="text-xs text-emerald-700 ml-auto flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Adresse bestätigt am {fmtDateTime(portalInfo.confirmed_at)}{portalInfo.confirmed_ip ? ` (IP ${portalInfo.confirmed_ip})` : ""}</span>
                : <span className="text-xs text-amber-700 ml-auto">Noch nicht bestätigt</span>}
            </SectionHead>
            <SectionBody k="portal">
            <div className="flex items-center gap-2">
              <div className="flex-1 text-sm font-mono text-slate-800 break-all" data-testid="auftrag-portal-url">{portalInfo.portal_url}</div>
              <Button size="sm" variant="outline" onClick={copyPortal} data-testid="auftrag-portal-copy"><Copy className="w-4 h-4 mr-1" /> Kopieren</Button>
              <Button size="sm" variant="outline" onClick={() => window.open(portalInfo.portal_url, "_blank")} data-testid="auftrag-portal-open"><ExternalLink className="w-4 h-4 mr-1" /> Öffnen</Button>
              {!isViewer && (
                <Button size="sm" variant="outline" onClick={resetPortal} disabled={resettingPortal} data-testid="auftrag-portal-reset"><RotateCw className="w-4 h-4 mr-1" /> Neu erzeugen</Button>
              )}
            </div>
            </SectionBody>
          </CardContent>
        </Card>
      )}

      <ZahlungsfristBox order={order} onChange={load} canEdit={["admin", "manager", "employee", "buchhaltung"].includes(user?.rolle)} collapseKey="zahlungsfrist" />

      <DruckparameterCard orderId={order.id} canEdit={["admin", "manager", "employee"].includes(user?.rolle)} positions={order.positionen} collapseKey="druckparameter" />
      <ProduktionSection order={order} user={user} onChanged={load} collapseKey="produktion" />
      <KundenteileSection order={order} user={user} onUpdated={setOrder} collapseKey="kundenteile" />

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {order.angebot_pdf_version > 0 && (
            <SectionBody k="positionen" part="belege">
            <div className="p-3 border-b bg-slate-50 flex items-center gap-2 flex-wrap">
              <FileDown className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-700">Angebot-PDF v{order.angebot_pdf_version}</span>
              <Button size="sm" variant="outline" onClick={() => openAuthedFile(`/pdf/angebot/${order.id}`, `Angebot_${order.auftragsnummer}.pdf`).catch((e) => toast.error(`Angebot-PDF konnte nicht geöffnet werden: ${e.message}`))} data-testid="auftrag-pdf-button">Öffnen</Button>
              {order.angebot_pdf_source === "sevdesk"
                ? <span className="text-xs text-emerald-700 ml-2" data-testid="mail-pdf-source">Mail enthält sevDesk-PDF</span>
                : order.angebot_pdf_source === "crm_kostenlos"
                  ? <span className="text-xs text-emerald-700 ml-2" data-testid="mail-pdf-source">CRM-Angebot {order.kostenlos_angebot_nummer} (0-€-Beleg, nicht in sevDesk)</span>
                  : <span className="text-xs text-slate-500 ml-2" data-testid="mail-pdf-source">Mail enthält lokales PDF (Fallback)</span>}
              {order.sevdesk_warning && <span className="text-xs text-amber-700 ml-3">⚠ {order.sevdesk_warning}</span>}
              {order.sevdesk_order_id && <span className="text-xs text-emerald-700 ml-3">✓ sevDesk-Order #{order.sevdesk_order_id}</span>}
              {order.sevdesk_angebot_id && ["admin", "manager"].includes(user?.rolle) && (
                <Button size="sm" variant="outline" onClick={reapplyLetterpaper} disabled={applyingLp} data-testid="auftrag-sevdesk-letterpaper-apply">
                  <RefreshCw className={`w-4 h-4 mr-1 ${applyingLp ? "animate-spin" : ""}`} /> Briefpapier neu anwenden
                </Button>
              )}
              <span className="mx-2 h-4 w-px bg-slate-300" />
              <Receipt className="w-4 h-4 text-slate-500" />
              {order.rechnungsnummer ? (
                <>
                  <span className="text-sm text-slate-700">Rechnung <span className="font-mono">{order.rechnungsnummer}</span> v{order.rechnung_pdf_version}</span>
                  <Button size="sm" variant="outline" onClick={() => openAuthedFile(`/pdf/rechnung/${order.id}`, `Rechnung_${order.rechnungsnummer || order.auftragsnummer}.pdf`).catch((e) => toast.error(`Rechnungs-PDF konnte nicht geöffnet werden: ${e.message}`))} data-testid="auftrag-rechnung-open">Öffnen</Button>
                  {!isViewer && <Button size="sm" variant="ghost" onClick={() => createInvoice()} disabled={invoiceBusy} data-testid="auftrag-rechnung-regenerate" title="PDF neu laden"><RotateCw className={`w-4 h-4 ${invoiceBusy ? "animate-spin" : ""}`} /></Button>}
                  <RechnungSevdeskStatus order={order} onChanged={load} />
                  <RechnungActions order={order} user={user} onChanged={load} />
                  {order.rechnung_skonto && (
                    <Badge variant="outline" className={order.rechnung_skonto.vermerkt ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-300"}
                      title={order.rechnung_skonto.text} data-testid="auftrag-rechnung-skonto">
                      {order.rechnung_skonto.vermerkt
                        ? `Skonto ${order.rechnung_skonto.prozent} % auf Rechnung vermerkt – Zahlbetrag ${fmtEUR(order.rechnung_skonto.zahlbetrag)}`
                        : `Skonto ${order.rechnung_skonto.prozent} % NICHT vermerkt – bitte in sevDesk prüfen (Zahlbetrag ${fmtEUR(order.rechnung_skonto.zahlbetrag)})`}
                    </Badge>
                  )}
                </>
              ) : order.kostenlos ? (
                <span title="0-€-Aufträge (Goodies/Muster) erhalten keine Rechnung – sevDesk unterstützt keine 0-€-Belege." data-testid="auftrag-rechnung-create-wrap">
                  <Button size="sm" variant="outline" disabled data-testid="auftrag-rechnung-create"><FileText className="w-4 h-4 mr-1" /> Rechnung erstellen</Button>
                  <span className="text-xs text-slate-500 ml-2" data-testid="auftrag-keine-rechnung-hint">{KEINE_RECHNUNG}</span>
                </span>
              ) : (["admin", "manager", "buchhaltung"].includes(user?.rolle) && !String(order.status).startsWith("storniert") && (
                <Button size="sm" variant="outline" onClick={() => setInvoiceDialog(true)} disabled={invoiceBusy} data-testid="auftrag-rechnung-create">{invoiceBusy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Rechnung wird erstellt…</> : <><FileText className="w-4 h-4 mr-1" /> Rechnung erstellen</>}</Button>
              ))}
              {!order.rechnungsnummer && order.rechnung_storniert && <RechnungSevdeskStatus order={order} onChanged={load} />}
              {order.storno_rechnung_nummer && <span className="text-xs text-slate-600" data-testid="auftrag-stornorechnung">Stornorechnung <span className="font-mono">{order.storno_rechnung_nummer}</span>{order.rechnung_storniert?.nummer ? ` zu ${order.rechnung_storniert.nummer}` : ""}</span>}
            </div>
            </SectionBody>
          )}
          <SectionHead k="positionen" label="Positionen" className="p-4 flex items-center gap-2" openClassName="border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Positionen</h2>
            <SectionSummary k="positionen">{order.positionen.length === 1 ? "1 Position" : `${order.positionen.length} Positionen`} · {fmtEUR(order.summe_brutto)}</SectionSummary>
            <div className="flex items-center gap-4 ml-auto">
            {!isViewer && <AddShippingButton order={order} locked={locked} onUpdated={setOrder} />}
            <div className="text-sm text-slate-600">
              Netto: <span className="font-mono font-semibold" data-testid="auftrag-summe-netto">{fmtEUR(order.summe_netto)}</span>
              <span className="mx-2 text-slate-300">|</span>
              Brutto: <span className="font-mono font-semibold text-slate-900" data-testid="auftrag-summe-brutto">{fmtEUR(order.summe_brutto)}</span>
            </div>
            </div>
          </SectionHead>
          <SectionBody k="positionen">
            <PositionsTable positionen={order.positionen} zeilen={order.summen?.zeilen} locked={locked} onUpdate={updatePos} onDelete={deletePos} onAdd={addPos} />
            <GesamtrabattSummen order={order} locked={locked} readOnly={isViewer} onUpdated={setOrder} />
          </SectionBody>
        </CardContent>
      </Card>

      {order.notiz_intern && (
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <SectionHead k="notiz" label="Interne Notiz" className="flex items-center gap-2" openClassName="mb-2">
              <div className="text-xs uppercase font-mono tracking-wider text-slate-500">Interne Notiz</div>
            </SectionHead>
            <SectionBody k="notiz">
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{order.notiz_intern}</div>
            </SectionBody>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-5">
          {(order.datei_import || order.anfrage_id) && (
            <SectionBody k="dateien" part="import">
              <FileImportStatus state={order.datei_import} link={order.datei_import?.url} endpoint={`/auftraege/${id}/datei-import`} canImport={!isViewer} onChange={load} />
            </SectionBody>
          )}
          <AttachmentsSection key={order.datei_import?.updated_at || "a"} orderId={id} kundeId={order.kunde_id} readOnly={isViewer} title="Dateien zum Auftrag" collapseKey="dateien"
            calc={{ jobs: kalk.jobs, allowedExt: kalk.allowedExt, positions: order.positionen, locked, canCalc: ["admin", "manager", "employee"].includes(user?.rolle), onOpen: setKalkAtt }} />
          <KalkulationDialog orderId={id} att={kalkAtt} onClose={() => setKalkAtt(null)} onStarted={kalk.reload} />
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-5">
          <EmailHistorySection orderId={id} collapseKey="emails" refreshKey={mailTick} />
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-5">
          <OrderShipmentsSection order={order} readOnly={isViewer} onChanged={load} onMailSent={() => setMailTick((n) => n + 1)} collapseKey="versand" />
        </CardContent>
      </Card>

      <TeamSection orderId={id} collapseKey="team" />

      <TasksSection orderId={id} collapseKey="aufgaben" />

      <Card className="border-slate-200">
        <CardContent className="p-5">
          <SectionHead k="kommentare" label="Kommentare" className="flex items-center gap-2" openClassName="mb-3">
            <h2 className="text-base font-semibold">Kommentare</h2>
          </SectionHead>
          <SectionBody k="kommentare">
            <Comments orderId={id} />
          </SectionBody>
        </CardContent>
      </Card>

      <AlertDialog open={confirmFreigabe} onOpenChange={setConfirmFreigabe}>
        <AlertDialogContent data-testid="freigabe-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Preise freigeben?</AlertDialogTitle>
            <AlertDialogDescription>
              Preise werden gesperrt und ein Stripe-Zahlungslink über <b>{fmtEUR(order.summe_brutto)}</b> erstellt. Fortfahren?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={doFreigabe} data-testid="freigabe-confirm-button">Freigeben</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="preisfreigabe-dialog">
          <DialogHeader>
            <DialogTitle>Preise freigeben & E-Mail versenden</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              {!preview.stripe_configured && !preview.kostenlos && (
                <div className="bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-md text-sm">
                  <b>Stripe nicht konfiguriert</b> — bitte zuerst in Einstellungen einen Secret Key hinterlegen.
                </div>
              )}
              {!preview.smtp_configured && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-md text-sm" data-testid="smtp-warning">
                  <b>SMTP nicht konfiguriert</b> — die E-Mail kann nicht versendet werden. Der Zahlungslink wird trotzdem am Auftrag gespeichert (Copy-Button).
                </div>
              )}
              <div className={`text-sm p-3 rounded-md border flex items-center gap-2 ${preview.is_within_business_hours ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="bh-status-preview">
                <Clock className="w-4 h-4" />
                {preview.is_within_business_hours
                  ? "Innerhalb Geschäftszeiten — Sofortversand"
                  : `Außerhalb Geschäftszeiten — Mail wird ${new Date(preview.next_business_start_utc).toLocaleString("de-DE")} gesendet`}
              </div>
              <div className="text-sm p-3 rounded-md border bg-slate-50 border-slate-200 text-slate-700 flex items-center gap-2" data-testid="preview-pdf-source">
                <FileDown className="w-4 h-4" />
                {preview.pdf_attachment_source === "crm_kostenlos"
                  ? <span data-testid="preview-pdf-source-kostenlos">Anhang: CRM-Angebot (0,00 €, eigener Nummernkreis) – {CRM_HINWEIS}. Keine Zahlung, kein Zahlungslink, keine Zahlungsfrist.</span>
                  : preview.pdf_attachment_source === "sevdesk"
                  ? "Anhang: sevDesk-PDF mit Briefpapier (falls sevDesk fehlschlägt: lokales PDF als Fallback)"
                  : "Anhang: lokales PDF (sevDesk-Master ist deaktiviert)"}
              </div>
              {preview.kostenlos && preview.sevdesk_angebot_vorhanden && (
                <div className="text-sm p-3 rounded-md border bg-amber-50 border-amber-300 text-amber-900" data-testid="preview-kostenlos-sevdesk-hint">
                  Es existiert noch ein sevDesk-Angebot. Bitte zuerst am Auftrag „sevDesk-Angebot ablehnen (Status 300) und CRM-Angebot verwenden“ wählen.
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Empfänger</label>
                  <input className="mt-1 w-full h-9 px-3 border border-slate-200 rounded-md text-sm" value={testMode ? (user?.email || "") : toEmail} disabled={testMode} onChange={(e) => setToEmail(e.target.value)} data-testid="preisfreigabe-email-input" />
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-3">
                    <Switch checked={sendEmail} onCheckedChange={setSendEmail} disabled={!preview.smtp_configured} data-testid="send-email-switch" />
                    <span className="text-sm">E-Mail versenden</span>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-md border-2 border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="test-mode-box">
                <FlaskConical className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Switch checked={testMode} onCheckedChange={setTestMode} disabled={!sendEmail} data-testid="test-mode-switch" />
                    <span className="text-sm font-medium text-amber-900">🧪 Testmodus — Mail an mich ({user?.email}) statt an Kunden senden</span>
                  </label>
                  {testMode && (
                    <div className="text-xs text-amber-800 mt-2" data-testid="test-mode-info">
                      Betreff wird mit <code className="bg-amber-100 px-1 rounded">[TEST]</code> versehen, im Body erscheint ein Banner. Der originale Empfänger <b>{toEmail || "(leer)"}</b> wird nicht kontaktiert.
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Betreff</label>
                <input className="mt-1 w-full h-9 px-3 border border-slate-200 rounded-md text-sm" value={testMode ? `[TEST] ${subject}` : subject} disabled={testMode} onChange={(e) => setSubject(e.target.value)} data-testid="preisfreigabe-subject-input" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={respectBh} onCheckedChange={setRespectBh} disabled={!sendEmail} data-testid="respect-bh-switch" />
                <span className="text-sm">Erst innerhalb Geschäftszeiten senden</span>
              </div>
              {preview?.pdf_attachment_source === "sevdesk" && (
                <label className="flex items-start gap-3 text-sm p-3 border border-slate-200 rounded-md" data-testid="allow-local-pdf-box">
                  <Switch checked={allowLocalPdf} onCheckedChange={setAllowLocalPdf} data-testid="allow-local-pdf-switch" />
                  <span>Trotzdem mit CRM-PDF senden (sevDesk nicht verfügbar)<span className="block text-xs text-slate-500">Standard aus: Schlägt sevDesk fehl, wird die Freigabe abgebrochen und keine Mail versendet.</span></span>
                </label>
              )}
              <div>
                <label className="text-sm font-medium">Vorschau</label>
                <div className="mt-1 border rounded-md bg-white overflow-auto max-h-[45vh] p-3" data-testid="email-html-preview">
                  {testMode && (
                    <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",color:"#92400E",padding:"12px 16px",marginBottom:"16px",borderRadius:"6px"}}>
                      🧪 <b>TESTMAIL</b> — wäre normal gegangen an: <code>{toEmail || "(kein Empfänger)"}</code>
                    </div>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: preview.html_body }} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Abbrechen</Button>
            <Button onClick={() => (((preview?.pdf_attachment_source === "sevdesk" && !order.sevdesk_angebot_id) || preview?.kostenlos) ? setOfferPreview(true) : doFreigabe())}
              disabled={releasing || (!preview?.stripe_configured && !preview?.kostenlos) || (preview?.kostenlos && preview?.sevdesk_angebot_vorhanden)} data-testid="freigabe-send-button">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {sendEmail ? "Freigeben & Senden" : "Freigeben"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DokumentVorschauDialog open={offerPreview} onOpenChange={setOfferPreview} orderId={id} typ="angebot" busy={releasing} kostenlos={!!preview?.kostenlos}
        onConfirm={async () => { await doFreigabe(); setOfferPreview(false); }} />

      <CancelOrderDialog order={order} open={confirmCancel} onOpenChange={setConfirmCancel} onDone={(o) => { setOrder(o); load(); }} />

      <Dialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
        <DialogContent data-testid="reactivate-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Undo2 className="w-5 h-5 text-emerald-600" /> Auftrag reaktivieren</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {order.storno_bezahlt ? (
              <p className="text-sm text-slate-700 p-3 rounded-md border border-slate-200 bg-slate-50" data-testid="reactivate-paid-hint">
                {["erstattet", "ausstehend"].includes(order.erstattung_status)
                  ? "Die Zahlung wurde erstattet: Der Auftrag geht zurück in Bearbeitung (vor der Preisfreigabe). Preise werden entsperrt – ein neues Angebot ist nötig."
                  : `Die Zahlung wurde nicht automatisch erstattet: Der Auftrag erhält wieder seinen vorherigen Status („${order.storno_vorher_status || "bezahlt"}“). Die offene Erstattungsaufgabe wird abgebrochen.`}
              </p>
            ) : <p className="text-sm text-slate-600">Wählen Sie den Zielstatus für den reaktivierten Auftrag.</p>}
            <div className={order.storno_bezahlt ? "hidden" : ""}>
              <Label>Zielstatus</Label>
              <Select value={reactivateTarget} onValueChange={setReactivateTarget}>
                <SelectTrigger data-testid="reactivate-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_bearbeitung">In Bearbeitung (Standard)</SelectItem>
                  <SelectItem value="wartet_auf_zahlung">Wartet auf Zahlung (falls Portal-Token gültig)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Ist der Portal-Token abgelaufen, wird automatisch auf „In Bearbeitung" fallback. Danach kannst Du den Portal-Link neu erzeugen.</p>
            </div>
            {order.sevdesk_angebot_id && <p className="text-xs text-blue-700">sevDesk-Angebot <span className="font-mono">{order.sevdesk_angebot_nummer}</span> wird automatisch auf „offen" zurückgesetzt.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateOpen(false)}>Abbrechen</Button>
            <Button onClick={doReactivate} disabled={reactivating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reactivate-confirm">
              {reactivating ? "Lädt…" : "Reaktivieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </SectionCollapseProvider>
  );
}
